import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, AlertCircle, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AppointmentCard } from "@/components/AppointmentCard";
import { AppointmentDetailsDialog } from "@/components/AppointmentDetailsDialog";
import {
  useStaffUpcomingAppointments,
  useStaffOngoingAppointments,
  useStaffHistoryAppointments,
} from "@/hooks/useStaffAppointments";
import type { StaffAppointment } from "@/types/appointments";

type TabValue = "upcoming" | "ongoing" | "past";

const MAX_CARDS = 5;

const TABS: { value: TabValue; label: string }[] = [
  { value: "upcoming", label: "Upcoming" },
  { value: "ongoing", label: "Ongoing" },
  { value: "past", label: "Past" },
];

function CardSkeleton() {
  return (
    <Card className="p-3 border-border">
      <div className="space-y-2.5">
        <div className="flex items-center gap-2">
          <Skeleton className="w-8 h-8 rounded-full shrink-0" />
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <Skeleton className="h-5 w-36" />
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
    </Card>
  );
}

interface AppointmentListProps {
  data: { results: StaffAppointment[]; count: number } | undefined;
  isLoading: boolean;
  error: unknown;
  variant: TabValue;
  onCardClick: (appt: StaffAppointment) => void;
  onViewAll: () => void;
}

function AppointmentList({
  data,
  isLoading,
  error,
  variant,
  onCardClick,
  onViewAll,
}: AppointmentListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load appointments.</AlertDescription>
      </Alert>
    );
  }

  if (!data || data.results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 rounded-xl border border-dashed border-border">
        <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
          <Calendar className="w-6 h-6 text-muted-foreground/50" />
        </div>
        <p className="z-body-sm text-muted-foreground">No appointments found</p>
      </div>
    );
  }

  const visible = data.results.slice(0, MAX_CARDS);
  const hasMore = data.count > MAX_CARDS;

  return (
    <div className="space-y-3">
      {visible.map((appt) => (
        <AppointmentCard
          key={appt.id}
          appointment={appt}
          onClick={() => onCardClick(appt)}
          variant={variant}
        />
      ))}
      {hasMore && (
        <Button variant="outline" className="w-full mt-1" onClick={onViewAll}>
          View all appointments
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      )}
    </div>
  );
}

export function AppointmentsWidget() {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<TabValue>("upcoming");
  const [selectedAppointment, setSelectedAppointment] =
    useState<StaffAppointment | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    data: upcomingData,
    isLoading: isLoadingUpcoming,
    error: upcomingError,
  } = useStaffUpcomingAppointments(1);
  const {
    data: ongoingData,
    isLoading: isLoadingOngoing,
    error: ongoingError,
  } = useStaffOngoingAppointments(1);
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useStaffHistoryAppointments(1);

  const handleCardClick = (appt: StaffAppointment) => {
    setSelectedAppointment(appt);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedAppointment(null);
  };

  const handleViewAll = () => {
    navigate("/settings/appointments/list");
  };

  const activeData =
    selectedTab === "upcoming"
      ? {
          data: upcomingData,
          isLoading: isLoadingUpcoming,
          error: upcomingError,
        }
      : selectedTab === "ongoing"
        ? {
            data: ongoingData,
            isLoading: isLoadingOngoing,
            error: ongoingError,
          }
        : {
            data: historyData,
            isLoading: isLoadingHistory,
            error: historyError,
          };

  return (
    <>
      <div className="flex flex-col border border-border rounded-xl overflow-hidden bg-card">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-border shrink-0">
          <h2 className="z-h2 text-foreground mb-3">Your Schedule</h2>
          <Tabs
            value={selectedTab}
            onValueChange={(v) => setSelectedTab(v as TabValue)}
          >
            <TabsList className="h-10">
              {TABS.map(({ value, label }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="font-heading text-sm font-medium px-5"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Scrollable card list — fixed height shows ~3 cards with room to scroll */}
        <div className="overflow-y-auto p-4 max-h-[340px]">
          <AppointmentList
            data={activeData.data}
            isLoading={activeData.isLoading}
            error={activeData.error}
            variant={selectedTab}
            onCardClick={handleCardClick}
            onViewAll={handleViewAll}
          />
        </div>
      </div>

      <AppointmentDetailsDialog
        appointment={selectedAppointment}
        open={isDialogOpen}
        onOpenChange={handleCloseDialog}
        isPast={selectedTab === "past"}
      />
    </>
  );
}
